import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  ListItemIcon
} from '@mui/material';
import { useNotification } from '../layouts/Layout';
import AddOrderForm from './AddOrderForm';
import * as projectService from '../../services/projectService'; 
import { Order as OrderInterface } from '../../services/projectService';
import { MoreVert as MoreVertIcon, Send, Delete, MailOutline as GmailIcon, FileCopy as DraftIcon } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
// Usunięto import 'api' bezpośrednio, zakładając, że projectService go obsługuje

interface OrdersTabProps {
  projectId: string;
}

// Używamy OrderInterface bezpośrednio z serwisu, aby uniknąć redefinicji
// i zapewnić spójność typów.
const OrdersTab: React.FC<OrdersTabProps> = ({ projectId }) => {
  const [orders, setOrders] = useState<OrderInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openOrderForm, setOpenOrderForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderInterface | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const { showNotification } = useNotification();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOrderId, setMenuOrderId] = useState<null | number>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, [projectId]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await projectService.getOrdersByProjectId(projectId);
      setOrders(data);
    } catch (err: any) {
      console.error("Error fetching orders:", err);
      setError(err.message || 'Nie udało się pobrać zamówień.');
      showNotification(`Błąd pobierania zamówień: ${err.message || 'Nieznany błąd'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, orderId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuOrderId(orderId);
  };

  const handleUpdateOrderStatus = async (orderId: number, newStatus: OrderInterface['status']) => {
    setLoading(true);
    try {
      // updateOrder przyjmuje ID jako string
      await projectService.updateOrder(orderId.toString(), { status: newStatus });
      fetchOrders();
      showNotification(`Status zamówienia zaktualizowany na "${newStatus}"`, 'success');
    } catch (err: any) {
      console.error("Error updating order status:", err);
      setError(err.message || 'Nie udało się zaktualizować statusu zamówienia.');
      showNotification(`Błąd aktualizacji statusu: ${err.message || 'Nieznany błąd'}`, 'error');
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleMenuClose = (event?: object) => { 
     if (event && typeof (event as any).stopPropagation === 'function') {
         (event as any).stopPropagation();
     }
    setAnchorEl(null);
    setMenuOrderId(null);
  };

  const handleEditClick = (orderId: number | null) => {
    handleMenuClose();
    if (orderId) {
        const orderToEdit = orders.find(order => order.id === orderId);
        if (orderToEdit) {
            setSelectedOrder(orderToEdit);
            setEditMode(true);
            setOpenOrderForm(true);
        }
    }
  };

  const handleDeleteClick = (orderId: number | null) => {
    handleMenuClose();
    if (orderId) {
        handleDeleteOrder(orderId);
    }
  };

  const handleCreateOrderDraft = async (orderId: number) => {
    setLoading(true);
    try {
      const result = await projectService.createOrderDraft(orderId.toString());
      showNotification(result.message || 'Draft emaila utworzony pomyślnie!', 'success');
      fetchOrders(); 
      if (result.data?.draftUrl) {
        window.open(result.data.draftUrl, '_blank');
      }
    } catch (err: any) {
      console.error("Error creating draft:", err);
      setError(err.message || 'Nie udało się utworzyć draftu emaila.');
      showNotification(`Błąd tworzenia draftu: ${err.response?.data?.message || err.message || 'Nieznany błąd'}`, 'error');
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleSendEmail = async (orderId: number) => {
    setLoading(true);
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order || !order.gmail_draft_id) {
        showNotification('Nie można wysłać wiadomości - brak ID draftu.', 'error');
        setLoading(false);
        return;
      }
      // Przekazujemy order.gmail_draft_id do sendGmailDraft
      const response = await projectService.sendGmailDraft(orderId.toString(), order.gmail_draft_id); 
      showNotification(response.message || 'Wiadomość wysłana pomyślnie', 'success');
      fetchOrders(); 
    } catch (err: any) {
      console.error('Błąd podczas wysyłania wiadomości Gmail:', err);
      if (err.response?.data?.requireReauth) {
         showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
      } 
      else if (err.response?.status === 404 || err.response?.data?.notFound) {
        showNotification(err.response?.data?.message || 'Wersja robocza nie została znaleziona.', 'warning');
        fetchOrders(); 
      } 
      else {
         showNotification(err.response?.data?.message || 'Wystąpił błąd podczas wysyłania wiadomości.', 'error');
      }
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleSendDirectEmail = async (orderId: number) => {
    setLoading(true);
    try {
      const response = await projectService.sendDirectEmail(orderId.toString());
      showNotification(response.message || 'Wiadomość wysłana pomyślnie', 'success');
      fetchOrders(); 
    } catch (err: any) {
      console.error('Błąd podczas wysyłania wiadomości Gmail:', err);
      if (err.response?.data?.requireReauth) {
         showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
      } else {
         showNotification(err.response?.data?.message || 'Wystąpił błąd podczas wysyłania wiadomości.', 'error');
      }
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleOpenOrderForm = () => {
    setEditMode(false);
    setSelectedOrder(null);
    setOpenOrderForm(true);
  };

  const handleOpenEditOrderForm = () => {
    if (selectedOrderIds.length === 1) {
      const orderToEdit = orders.find(order => order.id === selectedOrderIds[0]);
      if (orderToEdit) {
        setSelectedOrder(orderToEdit);
        setEditMode(true);
        setOpenOrderForm(true);
      }
    }
  };

  const handleCloseOrderForm = () => {
    setOpenOrderForm(false);
    setEditMode(false);
    setSelectedOrder(null);
  };

  const handleAddOrder = async (newOrderData: Omit<OrderInterface, 'id' | 'createdAt' | 'updatedAt' | 'order_id_display' | 'sequence_number' | 'gmail_draft_id' | 'gmail_message_id' | 'user' | 'project'>) => {
    setLoading(true);
    try {
      const orderPayload = { ...newOrderData, project_id: newOrderData.project_id || projectId };
      await projectService.createOrder(orderPayload);
      
      if (orderPayload.project_id && orderPayload.project_id !== projectId) {
        showNotification(`Zamówienie dodane pomyślnie do projektu ${orderPayload.project_id}.`, 'success');
      } else {
        showNotification('Zamówienie dodane pomyślnie.', 'success');
        fetchOrders(); 
      }
    } catch (err: any) {
      console.error("Error adding order:", err);
      setError(err.message || 'Nie udało się dodać zamówienia.');
      showNotification(`Błąd dodawania zamówienia: ${err.message || 'Nieznany błąd'}`, 'error');
    } finally {
      setLoading(false);
      handleCloseOrderForm();
    }
  };

  const handleEditOrder = async (editedOrderData: OrderInterface) => {
     setLoading(true);
    try {
      await projectService.updateOrder(editedOrderData.id.toString(), editedOrderData);
      fetchOrders(); 
      setSelectedOrderIds([]);
      showNotification('Zamówienie zaktualizowane pomyślnie.', 'success');
    } catch (err: any) {
      console.error("Error updating order:", err);
      setError(err.message || 'Nie udało się zaktualizować zamówienia.');
      showNotification(`Błąd aktualizacji zamówienia: ${err.message || 'Nieznany błąd'}`, 'error');
    } finally {
       setLoading(false);
       handleCloseOrderForm();
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    setLoading(true);
    try {
      await projectService.deleteOrder(orderId.toString());
      fetchOrders(); 
      setSelectedOrderIds(prev => prev.filter(id => id !== orderId)); 
      showNotification('Zamówienie usunięte pomyślnie.', 'success');
    } catch (err: any) {
      console.error("Error deleting order:", err);
      setError(err.message || 'Nie udało się usunąć zamówienia.');
      showNotification(`Błąd usuwania zamówienia: ${err.message || 'Nieznany błąd'}`, 'error');
    } finally {
      setLoading(false);
      handleCloseOrderForm(); 
    }
  };

  const handleSelectOrderItem = (id: number) => {
    const selectedIndex = selectedOrderIds.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedOrderIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedOrderIds.slice(1));
    } else if (selectedIndex === selectedOrderIds.length - 1) {
      newSelected = newSelected.concat(selectedOrderIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedOrderIds.slice(0, selectedIndex),
        selectedOrderIds.slice(selectedIndex + 1),
      );
    }
    setSelectedOrderIds(newSelected);
  };

  const handleSelectAllOrderItems = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = orders.map((order) => order.id);
      setSelectedOrderIds(newSelecteds);
      return;
    }
    setSelectedOrderIds([]);
  };

  const isOrderItemSelected = (id: number) => selectedOrderIds.indexOf(id) !== -1;

  const orderStatuses: OrderInterface['status'][] = ['Przygotowane', 'Draft gmail', 'Wysłane']; // Zaktualizowano statusy

  return (
    <Box>
      {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', my: 2 }} />}
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Zamówienia ({orders.length})
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <FormControl sx={{ minWidth: 180 }}>
              <Select
                value={statusFilter || ''}
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value || null)}
                displayEmpty
                inputProps={{ 'aria-label': 'Status' }}
                sx={{ 
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#aaa' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                  height: '40px'
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 300, boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)' }}}}
              >
                <MenuItem value="">Status: Wszystkie</MenuItem>
                {orderStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Button
            variant="contained"
            onClick={handleOpenOrderForm}
            disabled={loading} 
            sx={{ backgroundColor: '#004273', '&:hover': { backgroundColor: '#003660' }}}
          >
            Dodaj
          </Button>
        </Box>
      </Box>
      
      {!loading && !error && (
        <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 'none', border: '1px solid #eee' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell padding="checkbox" width="40px">
                  <Checkbox 
                    indeterminate={selectedOrderIds.length > 0 && selectedOrderIds.length < orders.length}
                    checked={orders.length > 0 && selectedOrderIds.length === orders.length} 
                    onChange={handleSelectAllOrderItems}
                    disabled={orders.length === 0} 
                  />
                </TableCell>
                <TableCell width="120px">ID</TableCell>
                <TableCell width="120px">Utworzył</TableCell>
                <TableCell>Kontrahent</TableCell>
                <TableCell>Treść zamówienia</TableCell>
                <TableCell width="120px">Data dostawy</TableCell>
                <TableCell>Osoba kontaktowa</TableCell>
                <TableCell width="100px">Status</TableCell>
                <TableCell width="50px" align="center">Akcje</TableCell> 
              </TableRow>
            </TableHead>
            <TableBody>
              {orders
                .filter(order => statusFilter ? order.status === statusFilter : true)
                .map((order) => {
                const isSelected = isOrderItemSelected(order.id);
                const contactPerson = (order.contact_person_name || order.contact_person_surname) 
                  ? `${order.contact_person_name || ''} ${order.contact_person_surname || ''}`.trim() + (order.contact_person_phone ? `, ${order.contact_person_phone}` : '')
                  : '-';
                const deliveryDateFormatted = order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('pl-PL') : '-';
                
                const statusStyles: Record<OrderInterface['status'] | string, { bgColor: string; color: string; text: string }> = {
                  'Przygotowane': { bgColor: '#E3F2FD', color: '#1976D2', text: 'Przygotowane' },
                  'Draft gmail': { bgColor: '#FFF8E1', color: '#FF8F00', text: 'Draft Gmail' },
                  'Wysłane': { bgColor: '#E8F5E9', color: '#2E7D32', text: 'Wysłane' },
                  // Usunięto niepotrzebne style
                };
                const currentStatusStyle = statusStyles[order.status] || { bgColor: '#f5f5f5', color: '#000', text: order.status };

                return (
                  <TableRow 
                    key={order.id} 
                    hover
                    onClick={() => handleSelectOrderItem(order.id)}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected} />
                    </TableCell>
                    <TableCell>{order.order_id_display || order.id}</TableCell>
                    <TableCell title={order.first_name && order.last_name ? `${order.first_name} ${order.last_name}` : ''}>
                      {order.first_name && order.last_name 
                        ? `${order.first_name.charAt(0)}${order.last_name.charAt(0)}`
                        : '-'}
                    </TableCell>
                    <TableCell>{order.contractor || '-'}</TableCell>
                    <TableCell>{order.order_content || '-'}</TableCell>
                    <TableCell>{deliveryDateFormatted}</TableCell>
                    <TableCell>{contactPerson}</TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          backgroundColor: currentStatusStyle.bgColor,
                          color: currentStatusStyle.color,
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: 'inline-block',
                          fontSize: '0.75rem',
                          fontWeight: 'medium',
                          textAlign: 'center'
                        }}
                      >
                        {currentStatusStyle.text}
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding="none">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, order.id)}
                        disabled={loading}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {orders.length === 0 && !loading && (
                 <TableRow>
                   <TableCell colSpan={8} align="center">Brak zamówień do wyświetlenia.</TableCell>
                 </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddOrderForm
        open={openOrderForm}
        onClose={handleCloseOrderForm}
        onAdd={handleAddOrder}
        onEdit={handleEditOrder}
        onDelete={() => selectedOrder && handleDeleteOrder(selectedOrder.id)} 
        editMode={editMode}
        orderData={selectedOrder}
        currentProjectId={projectId}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()} 
      >
        <MenuItem onClick={() => handleEditClick(menuOrderId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edytuj
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(menuOrderId)}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          Usuń
        </MenuItem>
        
        {menuOrderId && orders.find(order => order.id === menuOrderId)?.status === 'Przygotowane' && (
          <MenuItem onClick={() => menuOrderId && handleCreateOrderDraft(menuOrderId)}>
            <ListItemIcon>
              <DraftIcon fontSize="small" />
            </ListItemIcon>
            Utwórz draft
          </MenuItem>
        )}
        
        {menuOrderId && orders.find(order => order.id === menuOrderId)?.status === 'Draft gmail' && (
          <MenuItem onClick={() => menuOrderId && handleSendEmail(menuOrderId)}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            Wyślij (z draftu)
          </MenuItem>
        )}
        
        {menuOrderId && orders.find(order => order.id === menuOrderId)?.status === 'Przygotowane' && (
          <MenuItem onClick={() => menuOrderId && handleSendDirectEmail(menuOrderId)}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            Wyślij (bezpośrednio)
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default OrdersTab;
